import { useEffect, useMemo, useState } from 'react'
import api from '../../../helpers/apiRequester'
import { csvEscape, downloadCsv } from './shared'
import {
    OverlapEntry,
    buildOverlapBreakdown,
    clientRejectionReasons,
    money,
    reasonLabel,
    secondaryRejectionReasons,
    shortDate,
} from './analysisReasons'
import { AnalysisClientModal } from './AnalysisClientModal'
import styles from './runsTable.module.scss'

interface DuplicateConflict {
    [key: string]: unknown
}

export interface AnalysisClaim {
    agreementId: number | null
    policyNumber: string
    lender: string
    lenderResolved: string | null
    loanValue: number | null
    currentBalance: number | null
    monthlyRepayment: number | null
    lengthOfContract: string | number | null
    startDate: string | null
    endDate: string | null
    accountType: string | null
    agreementStatus: string | null
    quantum: number | null
    claimRejectionReason: string | null
    claimRejectionDetails?: Record<string, unknown> | null
    duplicateConflict?: Record<string, unknown> | null
}

export interface AnalysisClient {
    clientKey: string
    introducerClientId?: number | string | null
    name: string
    firstname?: string
    lastname?: string
    email: string
    dob?: string
    telephone?: string
    postcode?: string
    address?: string
    ccjs: number
    voluntaryArrangements: number
    clientTotalValue: number
    claimCount: number
    eligibleClaimCount: number
    clientRejectionReason: string | null
    clientRejectionDetails?: Record<string, unknown> | null
    /**
     * Every client-level gate that failed, headline first. Older analyses only
     * carry `clientRejectionReason`; use `clientRejectionReasons()` to read both.
     */
    clientRejectionReasons?: { reason: string; details?: Record<string, unknown> | null }[] | null
    /** The `viewthru_batch_entry` row the analysis read this person from. */
    entryId?: number | null
    rowNumber?: number | null
    documents?: { creditReport: boolean; amlReport: boolean; resultJson: boolean } | null
    duplicateConflicts?: DuplicateConflict[] | null
    duplicateClaims?: DuplicateConflict[] | null
    aml: {
        reportSaved: boolean
        identityScore: string | null
        identityResult: string | null
        identityPassed?: boolean
        screeningRecorded?: boolean
        hasPreviousIndustrySearch?: boolean
        industryMatchCount?: number
        deceasedGroFlag?: boolean
        deceasedHaloFlag?: boolean
        addressVerified: string | null
        electoralRoll: string | null
        peps: number
        sanctions: number
        rcas: number
        cifas: string | null
        deceasedGro: string | null
        deceasedHalo: string | null
        ccjs: number
        hasIva: boolean
    } | null
    claims: AnalysisClaim[]
}

export interface ViewThruBatchAnalysis {
    params: {
        commissionPercentage: number
        statutoryInterestPercentage: number
        minimumClientValue: number
        calculationDate: string
        cfaRequired: boolean
        origin?: string
        minimumLoanValue?: number
        minimumContractMonths?: number
        minimumMonthlyRepayment?: number
        maximumMonthlyRepayment?: number
        earliestAgreementStart?: string
        latestAgreementStart?: string
        allowedAgreementStatuses?: string[]
        amlDocumentRequired?: boolean
        identityCheckRequired?: boolean
        previousIndustrySearchRejected?: boolean
        deceasedRejected?: boolean
        bankruptcyRejected?: boolean
        preLatestDateValueRequired?: boolean
        adultAtAgreementStartRequired?: boolean
        agreementStatusChecked?: boolean
        /** Which CCJ tolerance this analysis ran under: 'none' (zero) or 'maxFour'. */
        ccjRule?: string
        maxClientCcjs?: number
        duplicateEmailChecked?: boolean
        duplicatePolicyNumberChecked?: boolean
        duplicateDeductionChecked?: boolean
    }
    counts: {
        clients: number
        agreements: number
        eligibleClients: number
        ineligibleClients: number
        duplicateClients?: number
        eligibleNoDups: number
        notEligibleDups: number
        eligibleAgreements: number
        failedAgreements: number
        clientsWithoutAgreements: number
        clientsWithAmlIssues: number
    }
    reasons: {
        clientReasons: Record<string, number>
        claimReasons: Record<string, number>
    }
    summary: {
        totalBookValue?: number
        averageClientValue?: number
    }
    eligibleClients: AnalysisClient[]
    ineligibleClients: AnalysisClient[]
    duplicateClients?: AnalysisClient[]
    eligibleNoDups: AnalysisClient[]
    notEligibleDups: AnalysisClient[]
    duplicateDeduction?: {
        checkedIntroducers?: string[]
        skippedIntroducers?: { source: string; reason: string }[]
        fundingSkipped?: string | null
        failed?: string | null
    }
}

// Labels and formatters live in analysisReasons.ts so the client modal can use
// them without importing this (much larger) module; re-exported for callers.
export { reasonLabel, money, clientRejectionReasons, secondaryRejectionReasons, buildOverlapBreakdown }
export type { OverlapEntry }

const date = shortDate

interface Gate {
    label: string
    applied: boolean
    note?: string
}

/** The gates the funding engine applies, in the order it applies them. */
const buildGates = (params: ViewThruBatchAnalysis['params']) => {
    const clientGates: Gate[] = [
        {
            label: 'Has at least one agreement carrying a policy / account number',
            applied: true,
            note: 'Agreements without one are dropped before valuation; duplicates keep the highest starting balance',
        },
        {
            label: `CCJs no more than ${params.maxClientCcjs ?? 0} and no voluntary arrangement or IVA`,
            applied: true,
        },
        {
            label: 'No bankruptcy on record',
            applied: Boolean(params.bankruptcyRejected),
            note: params.bankruptcyRejected ? undefined : 'Bankruptcy was not enforced by this analysis',
        },
        {
            label: `Has loan value from at least one agreement started before ${date(params.latestAgreementStart ?? null)}`,
            applied: Boolean(params.preLatestDateValueRequired),
            note: params.preLatestDateValueRequired
                ? 'Assessed across every agreement the client holds, matching the FairClaim API'
                : 'Not enforced by this analysis',
        },
        {
            label: 'CFA / signature date on record',
            applied: Boolean(params.cfaRequired),
            note: 'ViewThru credit reports contain no signature data, so this gate is skipped',
        },
        {
            label: `Total valuation across eligible agreements at least ${money(params.minimumClientValue)}`,
            applied: true,
        },
        {
            label: 'Client not already held by another introducer or funded batch',
            applied: Boolean(params.duplicateDeductionChecked),
            note: params.duplicateDeductionChecked
                ? 'A match moves the whole client to Duplicates and excludes them from Eligible'
                : 'Duplicate deduction was not run',
        },
        {
            label: 'AML identity document saved against the client',
            applied: Boolean(params.amlDocumentRequired),
            note: params.amlDocumentRequired
                ? undefined
                : `Only enforced for introducers configured to require it — "${params.origin || 'this origin'}" is not`,
        },
        {
            label: 'Identity check result is a pass',
            applied: Boolean(params.identityCheckRequired),
            note: params.identityCheckRequired
                ? 'Fail, Refer or a missing result all reject the client'
                : 'Identity result was not enforced by this analysis',
        },
        {
            label: 'No previous industry search on the credit file',
            applied: Boolean(params.previousIndustrySearchRejected),
            note: params.previousIndustrySearchRejected
                ? 'Either the bureau flag or any listed prior search rejects the client'
                : 'Previous industry searches were not enforced by this analysis',
        },
        {
            label: 'Not recorded as deceased (GRO or Halo)',
            applied: Boolean(params.deceasedRejected),
            note: params.deceasedRejected ? undefined : 'Deceased flags were not enforced by this analysis',
        },
    ]

    const claimGates: Gate[] = [
        {
            label: 'Policy / account number not already used by another introducer or funded batch',
            applied: Boolean(params.duplicateDeductionChecked),
            note: params.duplicateDeductionChecked
                ? 'A matching agreement moves the whole client to Duplicates'
                : 'Duplicate deduction was not run',
        },
        {
            label: `Agreement start date on or after ${date(params.earliestAgreementStart ?? null)}`,
            applied: true,
        },
        {
            label: `Agreement start date before ${date(params.latestAgreementStart ?? null)}`,
            applied: Boolean(params.latestAgreementStart),
            note: params.latestAgreementStart
                ? 'Agreements from this date onwards are excluded from the quantum'
                : 'No latest start date was enforced by this analysis',
        },
        {
            label: 'Client was 18 or over when the agreement started',
            applied: Boolean(params.adultAtAgreementStartRequired),
            note: params.adultAtAgreementStartRequired ? undefined : 'Not enforced by this analysis',
        },
        {
            label: `Agreement status is one of: ${(params.allowedAgreementStatuses || []).join(', ') || '—'}`,
            applied: Boolean(params.agreementStatusChecked),
            note: params.agreementStatusChecked
                ? 'Defaulted, written-off, disputed or repossessed agreements are excluded'
                : 'Agreement status was not enforced by this analysis',
        },
        {
            label: 'Agreement start date is valid and not in the future',
            applied: true,
            note: `Future is judged against the valuation date, ${date(params.calculationDate)}`,
        },
        {
            label: `Starting balance (or total payable) at least ${money(params.minimumLoanValue)}`,
            applied: true,
            note: 'Below this it is unlikely to be motor finance',
        },
        { label: 'Lender present and on the approved lender list', applied: true },
        {
            label: `Contract term present, non-zero and at least ${params.minimumContractMonths ?? 5} months`,
            applied: true,
        },
        {
            label: `Monthly repayment between ${money(params.minimumMonthlyRepayment)} and ${money(
                params.maximumMonthlyRepayment
            )}`,
            applied: true,
        },
    ]

    return { clientGates, claimGates }
}

const GateList = ({ gates }: { gates: Gate[] }) => (
    <ol className={styles.gateList}>
        {gates.map((gate) => (
            <li key={gate.label} className={gate.applied ? '' : styles.gateSkipped}>
                <span>{gate.label}</span>
                {gate.note && <small>{gate.note}</small>}
                {!gate.applied && <em>not applied</em>}
            </li>
        ))}
    </ol>
)

export type Outcome = 'eligible' | 'ineligible' | 'duplicate'
export type FlaggedClient = AnalysisClient & { outcome: Outcome }

export const OUTCOME_LABELS: Record<Outcome, string> = {
    eligible: 'Eligible',
    ineligible: 'Not eligible',
    duplicate: 'Duplicate',
}

const isEligibleOutcome = (outcome: Outcome) => outcome === 'eligible'

const DUPLICATE_CATEGORY_LABELS: Record<string, string> = {
    pre_credit_consolidated: 'Credit report check — consolidated clients',
    pre_credit_viewthru: 'Credit report check — earlier ViewThru check',
    email: 'Introducer email',
    phone: 'Introducer phone',
    surname_dob: 'Surname and date of birth',
    already_funded_email: 'Already funded — email',
    already_funded_phone: 'Already funded — phone',
    policy_number: 'Introducer agreement number',
    already_funded_policy: 'Already funded — agreement number',
}

export const duplicateCategoryKey = (conflict: DuplicateConflict | null | undefined) => {
    const type = String(conflict?.type || '').trim()
    if (type === 'pre_credit_report') {
        return String(conflict?.source || '') === 'consolidated'
            ? 'pre_credit_consolidated'
            : 'pre_credit_viewthru'
    }
    if (DUPLICATE_CATEGORY_LABELS[type]) return type
    return type || 'other'
}

export const duplicateCategoryLabel = (key: string) =>
    DUPLICATE_CATEGORY_LABELS[key] || key.replaceAll('_', ' ') || 'Unspecified match'

export const clientDuplicateCategories = (client: AnalysisClient) => {
    const keys = new Set<string>()
    for (const conflict of client.duplicateConflicts || []) {
        keys.add(duplicateCategoryKey(conflict))
    }
    for (const conflict of client.duplicateClaims || []) {
        keys.add(duplicateCategoryKey(conflict))
    }
    for (const claim of client.claims || []) {
        if (claim.duplicateConflict) keys.add(duplicateCategoryKey(claim.duplicateConflict))
    }
    return Array.from(keys)
}

export const buildDuplicateBreakdown = (clients: AnalysisClient[]) => {
    const counts = new Map<string, number>()
    for (const client of clients) {
        for (const key of clientDuplicateCategories(client)) {
            counts.set(key, (counts.get(key) || 0) + 1)
        }
    }
    return Array.from(counts.entries())
        .map(([key, count]) => ({ key, label: duplicateCategoryLabel(key), count }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
}

type IneligibleGroup = 'AML screening' | 'Documents' | 'Funding rules'

const INELIGIBLE_GROUPS: Record<string, IneligibleGroup> = {
    clientsExcludedIdentityCheckFailed: 'AML screening',
    clientsExcludedPreviousIndustrySearch: 'AML screening',
    clientsExcludedDeceased: 'AML screening',
    clientsWithCcjOrVA: 'AML screening',
    clientsWithBankruptcy: 'AML screening',
    clientsExcludedMissingAmlDocument: 'Documents',
    clientsExcludedMissingCreditReportDocument: 'Documents',
}

const INELIGIBLE_GROUP_ORDER: IneligibleGroup[] = ['AML screening', 'Documents', 'Funding rules']

const tally = (values: string[]) => {
    const counts = new Map<string, number>()
    for (const value of values) counts.set(value, (counts.get(value) || 0) + 1)
    return Array.from(counts.entries())
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([value, count]) => `${value} ×${count}`)
        .join(' · ')
}

/**
 * What the screening actually said, per rejection reason, so the breakdown
 * reads "Fail ×3 · Refer ×1 · No result ×2" rather than a bare count.
 */
const ineligibleReasonDetail = (reason: string, clients: AnalysisClient[]) => {
    const details = clients.map((client) => client.clientRejectionDetails || {})
    switch (reason) {
        case 'clientsExcludedIdentityCheckFailed':
            return tally(
                details.map((detail) => {
                    if (detail.screening_recorded === false) return 'Not screened'
                    const result = detail.identity_result
                    return result == null || result === '' ? 'No result' : String(result)
                })
            )
        case 'clientsExcludedPreviousIndustrySearch': {
            const matches = details.reduce(
                (total, detail) => total + (Number(detail.industry_match_count) || 0),
                0
            )
            const flaggedOnly = details.filter((detail) => !(Number(detail.industry_match_count) || 0)).length
            return [
                matches ? `${matches} prior ${matches === 1 ? 'search' : 'searches'} listed` : '',
                flaggedOnly ? `${flaggedOnly} on bureau flag only` : '',
            ]
                .filter(Boolean)
                .join(' · ')
        }
        case 'clientsExcludedDeceased':
            return tally(
                details.flatMap((detail) => [
                    ...(detail.deceased_gro && String(detail.deceased_gro).toLowerCase() !== 'false' ? ['GRO'] : []),
                    ...(detail.deceased_halo && String(detail.deceased_halo).toLowerCase() !== 'false' ? ['Halo'] : []),
                ])
            )
        case 'clientsWithCcjOrVA':
            return tally(
                details.map((detail) =>
                    Number(detail.voluntaryArrangements) > 0 ? 'IVA' : `${Number(detail.ccjs) || 0} CCJs`
                )
            )
        default:
            return ''
    }
}

export interface IneligibleBreakdownEntry {
    key: string
    label: string
    count: number
    detail: string
    value: number
}

export const buildIneligibleBreakdown = (clients: AnalysisClient[]) => {
    const byReason = new Map<string, AnalysisClient[]>()
    for (const client of clients) {
        const key = client.clientRejectionReason || 'unspecified'
        const bucket = byReason.get(key) || []
        bucket.push(client)
        byReason.set(key, bucket)
    }
    const entries: IneligibleBreakdownEntry[] = Array.from(byReason.entries()).map(([key, group]) => ({
        key,
        label: key === 'unspecified' ? 'No reason recorded' : reasonLabel(key),
        count: group.length,
        detail: ineligibleReasonDetail(key, group),
        value: group.reduce((total, client) => total + (client.clientTotalValue || 0), 0),
    }))
    entries.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))

    return INELIGIBLE_GROUP_ORDER.map((group) => ({
        group,
        entries: entries.filter((entry) => (INELIGIBLE_GROUPS[entry.key] || 'Funding rules') === group),
    })).filter((section) => section.entries.length > 0)
}

/** Agreement-level failures among the not-eligible clients only. */
export const buildIneligibleClaimBreakdown = (clients: AnalysisClient[]) => {
    const counts = new Map<string, { clients: number; agreements: number }>()
    for (const client of clients) {
        const seen = new Set<string>()
        for (const claim of client.claims || []) {
            const reason = claim.claimRejectionReason
            if (!reason) continue
            const entry = counts.get(reason) || { clients: 0, agreements: 0 }
            entry.agreements += 1
            if (!seen.has(reason)) {
                entry.clients += 1
                seen.add(reason)
            }
            counts.set(reason, entry)
        }
    }
    return Array.from(counts.entries())
        .map(([key, entry]) => ({ key, label: reasonLabel(key), ...entry }))
        .sort((a, b) => b.clients - a.clients || b.agreements - a.agreements || a.label.localeCompare(b.label))
}

const csvNumber = (value: number | null | undefined) =>
    typeof value === 'number' && Number.isFinite(value) ? value.toFixed(2) : ''

const joinList = (values: Array<string | null | undefined>) =>
    values.filter((value): value is string => Boolean(value && String(value).trim())).join(' | ')

const agreementLabel = (claim: AnalysisClaim) => {
    const policy = claim.policyNumber || `agreement-${claim.agreementId || 'unknown'}`
    const lender = claim.lenderResolved || claim.lender
    return lender ? `${policy} (${lender})` : policy
}

const ANALYSIS_CLIENT_CSV_COLUMNS: {
    header: string
    value: (client: FlaggedClient) => string | number | null | undefined
}[] = [
    { header: 'outcome', value: (client) => client.outcome },
    { header: 'client_id', value: (client) => client.introducerClientId ?? '' },
    { header: 'name', value: (client) => client.name },
    { header: 'dob', value: (client) => client.dob || '' },
    { header: 'email', value: (client) => client.email },
    { header: 'telephone', value: (client) => client.telephone || '' },
    { header: 'postcode', value: (client) => client.postcode || '' },
    { header: 'address', value: (client) => client.address || '' },
    { header: 'ccjs', value: (client) => client.aml?.ccjs ?? client.ccjs },
    {
        header: 'voluntary_arrangement',
        value: (client) => (client.aml?.hasIva || client.voluntaryArrangements ? 'yes' : 'no'),
    },
    { header: 'aml_report_saved', value: (client) => (client.aml?.reportSaved ? 'yes' : 'no') },
    { header: 'aml_identity', value: (client) => client.aml?.identityResult || client.aml?.identityScore || '' },
    { header: 'aml_industry_searches', value: (client) => String(client.aml?.industryMatchCount ?? 0) },
    {
        header: 'aml_deceased',
        value: (client) => (client.aml?.deceasedGroFlag || client.aml?.deceasedHaloFlag ? 'yes' : 'no'),
    },
    { header: 'aml_address_verified', value: (client) => client.aml?.addressVerified || '' },
    { header: 'aml_electoral_roll', value: (client) => client.aml?.electoralRoll || '' },
    { header: 'aml_peps', value: (client) => client.aml?.peps ?? 0 },
    { header: 'aml_sanctions', value: (client) => client.aml?.sanctions ?? 0 },
    { header: 'aml_rcas', value: (client) => client.aml?.rcas ?? 0 },
    { header: 'aml_cifas', value: (client) => client.aml?.cifas || '' },
    { header: 'client_value', value: (client) => csvNumber(client.clientTotalValue) },
    { header: 'agreement_count', value: (client) => client.claimCount },
    { header: 'eligible_agreement_count', value: (client) => client.eligibleClaimCount },
    {
        header: 'rejected_agreement_count',
        value: (client) => Math.max(0, (client.claimCount || 0) - (client.eligibleClaimCount || 0)),
    },
    {
        header: 'client_rejection_reason',
        value: (client) =>
            client.outcome === 'duplicate'
                ? 'Duplicate'
                : isEligibleOutcome(client.outcome)
                  ? ''
                  : reasonLabel(client.clientRejectionReason),
    },
    { header: 'client_rejection_code', value: (client) => client.clientRejectionReason || '' },
    {
        header: 'other_client_rejection_reasons',
        value: (client) =>
            client.outcome === 'ineligible' ? secondaryRejectionReasons(client).map(reasonLabel).join(' | ') : '',
    },
    {
        header: 'client_rejection_details',
        value: (client) =>
            client.clientRejectionDetails ? JSON.stringify(client.clientRejectionDetails) : '',
    },
    {
        header: 'duplicate_conflicts',
        value: (client) => client.duplicateConflicts?.length ? JSON.stringify(client.duplicateConflicts) : '',
    },
    {
        header: 'eligible_agreements',
        value: (client) =>
            joinList(
                client.claims
                    .filter((claim) => !claim.claimRejectionReason)
                    .map((claim) => agreementLabel(claim))
            ),
    },
    {
        header: 'rejected_agreements',
        value: (client) =>
            joinList(
                client.claims
                    .filter((claim) => claim.claimRejectionReason)
                    .map((claim) => `${agreementLabel(claim)}: ${reasonLabel(claim.claimRejectionReason)}`)
            ),
    },
    {
        header: 'agreement_outcomes',
        value: (client) =>
            joinList(
                client.claims.map(
                    (claim) => `${agreementLabel(claim)}=${reasonLabel(claim.claimRejectionReason)}`
                )
            ),
    },
]

export interface AnalysisCsvColumn<T = FlaggedClient> {
    header: string
    value: (client: T) => string | number | null | undefined
}

/**
 * `leadingColumns` lets the all-batches page prefix each row with the batch
 * it came from while keeping one definition of the client columns.
 */
export const buildAnalysisClientsCsv = <T extends FlaggedClient>(
    clients: T[],
    leadingColumns: AnalysisCsvColumn<T>[] = []
) => {
    const columns: AnalysisCsvColumn<T>[] = [...leadingColumns, ...ANALYSIS_CLIENT_CSV_COLUMNS]
    const lines = [
        columns.map((column) => csvEscape(column.header)).join(','),
        ...clients.map((client) => columns.map((column) => csvEscape(column.value(client))).join(',')),
    ]
    return lines.join('\n')
}

export const analysisCsvFilename = (filenameBase: string, suffix: string) =>
    `${safeFilename(filenameBase)}-${suffix}.csv`

const safeFilename = (filenameBase: string) =>
    String(filenameBase || 'viewthru-batch')
        .replace(/[^\w.-]+/g, '_')
        .replace(/^_+|_+$/g, '') || 'viewthru-batch'

const downloadAnalysisClientsCsv = (clients: FlaggedClient[], filenameBase: string, suffix: string) => {
    if (!clients.length) return 0
    downloadCsv(`${safeFilename(filenameBase)}-analysis-${suffix}.csv`, buildAnalysisClientsCsv(clients))
    return clients.length
}

const ClientList = ({
    clients,
    highlightReason,
    onOpen,
}: {
    clients: FlaggedClient[]
    highlightReason: string | null
    /** Opens the client's record, documents and eligibility verdict in a modal. */
    onOpen?: (client: FlaggedClient) => void
}) => {
    if (!clients.length) {
        return <p className={styles.analysisEmpty}>No clients match these filters.</p>
    }

    return (
        <div className={styles.analysisClients}>
            {clients.map((client) => {
                const eligible = isEligibleOutcome(client.outcome)
                const alsoFailed = client.outcome === 'ineligible' ? secondaryRejectionReasons(client) : []
                return (
                <details key={client.clientKey} className={styles.analysisClient}>
                    <summary>
                        <span className={styles.analysisClientIdentity}>
                            <strong>{client.name || 'Unnamed client'}</strong>
                            <small>{client.email || 'No email'}</small>
                        </span>
                        <span className={styles.analysisClientValue}>
                            <strong>{money(client.clientTotalValue)}</strong>
                            <small>
                                {client.eligibleClaimCount}/{client.claimCount} agreements eligible
                            </small>
                        </span>
                        <span
                            className={`${styles.analysisClientOutcome} ${
                                eligible ? styles.analysisOk : styles.analysisWarn
                            }`}
                            title={alsoFailed.length ? `Also failed: ${alsoFailed.map(reasonLabel).join(', ')}` : undefined}
                        >
                            {client.outcome === 'ineligible'
                                ? reasonLabel(client.clientRejectionReason)
                                : OUTCOME_LABELS[client.outcome]}
                            {alsoFailed.length > 0 && <small> +{alsoFailed.length} more</small>}
                        </span>
                    </summary>

                    {(onOpen || alsoFailed.length > 0) && (
                        <div className={styles.analysisClientActions}>
                            {alsoFailed.length > 0 && (
                                <span>
                                    Also failed:{' '}
                                    {alsoFailed.map((reason) => (
                                        <em key={reason}>{reasonLabel(reason)}</em>
                                    ))}
                                </span>
                            )}
                            {onOpen && (
                                <button type="button" onClick={() => onOpen(client)}>
                                    Open record &amp; PDFs
                                </button>
                            )}
                        </div>
                    )}

                    {Boolean(client.duplicateConflicts?.length) && (
                        <div className={styles.duplicateConflicts}>
                            <strong>Duplicate conflicts</strong>
                            <ul>
                                {client.duplicateConflicts!.map((conflict, index) => (
                                    <li key={index}>
                                        {Object.entries(conflict)
                                            .filter(([, value]) => value !== null && value !== undefined && value !== '')
                                            .map(([key, value]) => `${key.replaceAll('_', ' ')}: ${
                                                typeof value === 'object' ? JSON.stringify(value) : String(value)
                                            }`)
                                            .join(' · ') || 'Duplicate match'}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className={styles.analysisAml}>
                        <strong>AML</strong>
                        <span>Report: {client.aml?.reportSaved ? 'Saved' : 'Missing'}</span>
                        <span>Identity: {client.aml?.identityResult || client.aml?.identityScore || '—'}</span>
                        <span>
                            Industry searches:{' '}
                            {client.aml?.hasPreviousIndustrySearch || (client.aml?.industryMatchCount ?? 0) > 0
                                ? `Yes (${client.aml?.industryMatchCount ?? 0})`
                                : 'None'}
                        </span>
                        <span>
                            Deceased: {client.aml?.deceasedGroFlag || client.aml?.deceasedHaloFlag ? 'Yes' : 'No'}
                        </span>
                        <span>Address verified: {client.aml?.addressVerified || '—'}</span>
                        <span>Electoral roll: {client.aml?.electoralRoll || '—'}</span>
                        <span>CCJs: {client.aml?.ccjs ?? client.ccjs}</span>
                        <span>IVA: {client.aml?.hasIva ? 'Yes' : 'No'}</span>
                        <span>PEPs: {client.aml?.peps ?? 0}</span>
                        <span>Sanctions: {client.aml?.sanctions ?? 0}</span>
                        <span>RCAs: {client.aml?.rcas ?? 0}</span>
                        <span>CIFAS: {client.aml?.cifas || '—'}</span>
                    </div>

                    <div className={styles.tableWrapper}>
                        <table className={`${styles.runsTable} ${styles.analysisTable}`}>
                            <thead>
                                <tr>
                                    <th>Agreement</th>
                                    <th>Lender</th>
                                    <th>Start</th>
                                    <th>Starting balance</th>
                                    <th>Current balance</th>
                                    <th>Monthly</th>
                                    <th>Term</th>
                                    <th>Valuation</th>
                                    <th>Outcome</th>
                                </tr>
                            </thead>
                            <tbody>
                                {client.claims.map((claim, index) => (
                                    <tr
                                        key={claim.agreementId ?? `${client.clientKey}-${index}`}
                                        className={
                                            highlightReason &&
                                            claim.claimRejectionReason === highlightReason
                                                ? styles.analysisRowMatch
                                                : ''
                                        }
                                    >
                                        <td>{claim.policyNumber || '—'}</td>
                                        <td>{claim.lender || '—'}</td>
                                        <td>{date(claim.startDate)}</td>
                                        <td>{money(claim.loanValue)}</td>
                                        <td>{money(claim.currentBalance)}</td>
                                        <td>{money(claim.monthlyRepayment)}</td>
                                        <td>{claim.lengthOfContract ? `${claim.lengthOfContract} months` : '—'}</td>
                                        <td>{money(claim.quantum)}</td>
                                        <td>
                                            {claim.duplicateConflict
                                                ? `Duplicate deducted · ${String(
                                                      claim.duplicateConflict.source || 'existing data'
                                                  )}`
                                                : reasonLabel(claim.claimRejectionReason)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </details>
                )
            })}
        </div>
    )
}

type StatusFilter = 'all' | Outcome
type SortOrder = 'valueDesc' | 'valueAsc' | 'name' | 'agreements'

interface ImportPreviewRow {
    clientId: number | string | null
    clientRef: string
    name: string
    email: string
    eligibleClaimCount: number
    value: number | null
    matchedBy?: string | null
    file?: { key: string } | null
    reasons?: string[]
}

interface ImportPreview {
    prefix?: string
    counts?: Record<string, number>
    importable?: ImportPreviewRow[]
    blocked?: ImportPreviewRow[]
    alreadyImportedClients?: ImportPreviewRow[]
    unmatchedFiles?: { key: string; name?: string; dob?: string | null; email: string; errors: string[] }[]
    [key: string]: unknown
}

interface ImportRun {
    id: string
    status: 'preparing' | 'running' | 'complete' | 'failed'
    total: number
    processed: number
    imported: number
    errors: number
    blocked: number
    currentClient?: string | null
    errorMessage?: string | null
}

const PREVIEW_ROW_LIMIT = 10

const countReasonsAcrossRows = (rows: ImportPreviewRow[]) => {
    const counts: Record<string, number> = {}
    rows.forEach((row) => {
        (row.reasons || ['Blocked without a stated reason']).forEach((reason) => {
            counts[reason] = (counts[reason] || 0) + 1
        })
    })
    return Object.entries(counts).sort((a, b) => b[1] - a[1])
}

const fileName = (key: string | undefined | null) =>
    key ? key.slice(key.lastIndexOf('/') + 1) : '—'

const plural = (count: number, word: string) => (count === 1 ? word : `${word}s`)

const PreviewRowTable = ({ rows, caption }: { rows: ImportPreviewRow[]; caption: string }) => {
    if (!rows.length) return null
    const shown = rows.slice(0, PREVIEW_ROW_LIMIT)

    return (
        <details className={styles.importRows}>
            <summary>
                {caption}
                <small>
                    {rows.length > PREVIEW_ROW_LIMIT
                        ? `showing the first ${PREVIEW_ROW_LIMIT} of ${rows.length}`
                        : `${rows.length} total`}
                </small>
            </summary>
            <div className={styles.tableWrapper}>
                <table className={styles.runsTable}>
                    <thead>
                        <tr>
                            <th>Client</th>
                            <th>Email</th>
                            <th>Claims</th>
                            <th>Value</th>
                            <th>Source file</th>
                            <th>Matched on</th>
                            <th>Reason</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((row) => (
                            <tr key={row.clientRef}>
                                <td>{row.name || 'Unnamed client'}</td>
                                <td>{row.email || '—'}</td>
                                <td>{row.eligibleClaimCount}</td>
                                <td>{money(row.value)}</td>
                                <td>{fileName(row.file?.key)}</td>
                                <td>{row.matchedBy || '—'}</td>
                                <td>{row.reasons?.length ? row.reasons.join('; ') : 'Ready to import'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </details>
    )
}

const humanize = (value: string) =>
    value.replace(/([a-z])([A-Z])/g, '$1 $2').replaceAll('_', ' ')

const DuplicateCoverage = ({
    deduction,
}: {
    deduction: ViewThruBatchAnalysis['duplicateDeduction']
}) => {
    if (!deduction) return null

    const skipped = deduction.skippedIntroducers || []
    if (!deduction.failed && !deduction.fundingSkipped && !skipped.length) return null

    return (
        <p className={styles.detailError} role="status">
            {deduction.failed
                ? `Duplicate deduction did not run: ${deduction.failed}. Eligible and not eligible are unaffected.`
                : 'Duplicate deduction ran against a partial set of sources.'}
            {deduction.fundingSkipped && ` Funded book not compared: ${deduction.fundingSkipped}.`}
            {skipped.length > 0 &&
                ` Not compared: ${skipped
                    .map((entry) => `${entry.source} (${entry.reason})`)
                    .join(', ')}.`}
        </p>
    )
}

const ImportPanel = ({
    batchId,
    onImported,
}: {
    batchId: string
    onImported: () => void | Promise<void>
}) => {
    // Matches the introducer registry's importPath for Sentinel Reporting —
    // the directory the signature text files are uploaded to.
    const [dataPath, setDataPath] = useState('srds/import/signatures')
    const [subdirectory, setSubdirectory] = useState('')
    const [preview, setPreview] = useState<ImportPreview | null>(null)
    const [busy, setBusy] = useState<'preview' | 'commit' | null>(null)
    const [message, setMessage] = useState<{ tone: 'error' | 'success'; text: string } | null>(null)
    const [importRunId, setImportRunId] = useState<string | null>(() =>
        window.localStorage.getItem(`viewthru-import-run:${batchId}`)
    )
    const [importRun, setImportRun] = useState<ImportRun | null>(null)

    const requestData = { batchId, dataPath: dataPath.trim(), subdirectory: subdirectory.trim() }
    const canSubmit = Boolean(requestData.dataPath && !busy)

    const runPreview = async () => {
        if (!canSubmit) return
        setBusy('preview')
        setMessage(null)
        try {
            const response = await api.post('viewthru-batch-import-preview', requestData)
            if (!response.data?.success) throw new Error(response.data?.message || 'Could not preview the import.')
            setPreview(response.data)
        } catch (error: any) {
            setPreview(null)
            setMessage({
                tone: 'error',
                text: error?.response?.data?.message || error?.message || 'Could not preview the import.',
            })
        } finally {
            setBusy(null)
        }
    }

    useEffect(() => {
        if (!importRunId) return
        let stopped = false
        let timer: ReturnType<typeof setTimeout> | null = null

        const poll = async () => {
            try {
                const response = await api.post('viewthru-batch-import-status', { runId: importRunId })
                if (stopped) return
                if (!response.data?.success || !response.data?.run) {
                    throw new Error(response.data?.message || 'Could not read import progress.')
                }

                const run = response.data.run as ImportRun
                setImportRun(run)
                if (run.status === 'complete' || run.status === 'failed') {
                    window.localStorage.removeItem(`viewthru-import-run:${batchId}`)
                    setImportRunId(null)
                    setBusy(null)
                    if (run.status === 'complete') {
                        setMessage({
                            tone: run.errors ? 'error' : 'success',
                            text: run.errors
                                ? `Imported ${run.imported} clients; ${run.errors} failed.`
                                : `Imported ${run.imported} clients into Sentinel Reporting.`,
                        })
                        setPreview(null)
                        await onImported()
                    } else {
                        setMessage({
                            tone: 'error',
                            text: run.errorMessage || 'The import stopped unexpectedly.',
                        })
                    }
                    return
                }
                setBusy('commit')
                timer = setTimeout(poll, 1000)
            } catch (error: any) {
                if (stopped) return
                // Keep the run ID and retry: a transient dashboard request must
                // not make a live background import look as though it stopped.
                setMessage({
                    tone: 'error',
                    text:
                        error?.response?.data?.message ||
                        error?.message ||
                        'Progress is temporarily unavailable; the import is still running.',
                })
                timer = setTimeout(poll, 3000)
            }
        }

        setBusy('commit')
        void poll()
        return () => {
            stopped = true
            if (timer) clearTimeout(timer)
        }
    }, [batchId, importRunId, onImported])

    const commit = async () => {
        if (!preview || busy) return
        if (
            !window.confirm(
                `Import ${preview.counts?.importable || 0} eligible, duplicate-free clients into Sentinel Reporting?`
            )
        ) {
            return
        }
        setBusy('commit')
        setMessage(null)
        try {
            const response = await api.post('viewthru-batch-import-start', requestData)
            const run = response.data?.run as ImportRun | undefined
            if (!response.data?.success || !run?.id) {
                throw new Error(response.data?.message || 'Could not start the import.')
            }
            window.localStorage.setItem(`viewthru-import-run:${batchId}`, run.id)
            setImportRun(run)
            setImportRunId(run.id)
        } catch (error: any) {
            setMessage({
                tone: 'error',
                text: error?.response?.data?.message || error?.message || 'Could not complete the import.',
            })
            setBusy(null)
        }
    }

    const totals = preview?.counts || {}
    const importableRows = preview?.importable || []
    const blockedRows = preview?.blocked || []
    const blockedReasons = countReasonsAcrossRows(blockedRows)
    const unmatchedFiles = preview?.unmatchedFiles || []
    const importableCount = preview?.counts?.importable || 0
    const eligibleTotal = Number(totals.eligible || 0)
    const dupTotal = Number(totals.duplicates || 0)
    const alreadyImportedRows = preview?.alreadyImportedClients || []

    return (
        <section
            className={styles.importPanel}
            aria-labelledby="viewthru-import-title"
            aria-busy={Boolean(busy)}
        >
            <div>
                <h4 id="viewthru-import-title">Import eligible clients</h4>
                <p>
                    Upload the signature text files to the data path below, then preview. Each file is
                    matched to an eligible client by email, or by name and date of birth when the
                    batch spreadsheet carried no email. Clients already held in Sentinel Reporting are
                    skipped, so scanning twice is safe.
                </p>
            </div>
            <div className={styles.importFields}>
                <label>
                    <span className={styles.detailLabel}>Bucket</span>
                    <input value="sentinelgeneratedfiles" disabled aria-label="Bucket" />
                </label>
                <label>
                    <span className={styles.detailLabel}>Data path</span>
                    <input
                        value={dataPath}
                        onChange={(event) => {
                            setDataPath(event.target.value)
                            setPreview(null)
                        }}
                        placeholder="Path to generated data"
                        disabled={Boolean(busy)}
                        required
                    />
                </label>
                <label>
                    <span className={styles.detailLabel}>Subdirectory (optional)</span>
                    <input
                        value={subdirectory}
                        onChange={(event) => {
                            setSubdirectory(event.target.value)
                            setPreview(null)
                        }}
                        placeholder="Leave blank to scan everything below"
                        disabled={Boolean(busy)}
                    />
                </label>
                <button type="button" onClick={() => void runPreview()} disabled={!canSubmit}>
                    {busy === 'preview' ? 'Previewing…' : 'Preview import'}
                </button>
            </div>

            {message && (
                <p
                    className={message.tone === 'error' ? styles.detailError : styles.importSuccess}
                    role={message.tone === 'error' ? 'alert' : 'status'}
                >
                    {message.text}
                </p>
            )}

            {importRun && (importRun.status === 'preparing' || importRun.status === 'running') && (
                <div className={styles.importProgress} role="status" aria-live="polite">
                    <div>
                        <strong>
                            {importRun.status === 'preparing'
                                ? 'Preparing import'
                                : `Importing client ${Math.min(importRun.processed + 1, importRun.total)} of ${
                                      importRun.total
                                  }`}
                        </strong>
                        <span>
                            {importRun.imported} imported · {importRun.errors} failed · {importRun.blocked} blocked
                        </span>
                    </div>
                    <progress
                        max={Math.max(importRun.total, 1)}
                        value={importRun.status === 'preparing' ? undefined : importRun.processed}
                    />
                    {importRun.currentClient && (
                        <small>
                            {importRun.status === 'preparing' ? importRun.currentClient : `Last processed: ${importRun.currentClient}`}
                        </small>
                    )}
                    <small>
                        This import continues on the server if you leave this page. Return to this batch to reconnect
                        to its progress.
                    </small>
                </div>
            )}

            {preview && (
                <div className={styles.importPreview}>
                    <div className={styles.importTotals}>
                        {Object.entries(totals).map(([label, value]) => (
                            <span key={label}>
                                <strong>{String(value ?? 0)}</strong> {humanize(label)}
                            </span>
                        ))}
                    </div>

                    {eligibleTotal > 0 && (
                        <p>
                            {eligibleTotal} duplicate-free eligible {plural(eligibleTotal, 'client')}:{' '}
                            {importableCount} ready and {blockedRows.length} blocked. The {dupTotal}{' '}
                            {plural(dupTotal, 'duplicate')} {dupTotal === 1 ? 'is' : 'are'} listed in the
                            Duplicates outcome above and excluded from this import.
                            {alreadyImportedRows.length > 0 &&
                                ` ${alreadyImportedRows.length} of those ${plural(
                                    dupTotal,
                                    'duplicate'
                                )} ${alreadyImportedRows.length === 1 ? 'is a person' : 'are people'} this import already placed in Sentinel Reporting.`}
                        </p>
                    )}

                    {preview.prefix && (
                        <p>
                            Scanned s3://sentinelgeneratedfiles/{preview.prefix}
                            {unmatchedFiles.length > 0 &&
                                ` · ${unmatchedFiles.length} text ${
                                    unmatchedFiles.length === 1 ? 'file' : 'files'
                                } matched no eligible client`}
                        </p>
                    )}

                    {blockedReasons.length > 0 && (
                        <div className={styles.importReasons}>
                            <strong>Why clients are blocked</strong>
                            <ul>
                                {blockedReasons.map(([reason, count]) => (
                                    <li key={reason}>
                                        <span>{reason}</span>
                                        <span>{count}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <PreviewRowTable rows={importableRows} caption="Clients ready to import" />
                    <PreviewRowTable rows={blockedRows} caption="Blocked clients" />
                    <PreviewRowTable
                        rows={alreadyImportedRows}
                        caption="Clients already held in Sentinel Reporting"
                    />

                    {unmatchedFiles.length > 0 && (
                        <details className={styles.importRows}>
                            <summary>
                                Text files that matched no eligible client
                                <small>
                                    {unmatchedFiles.length > PREVIEW_ROW_LIMIT
                                        ? `showing the first ${PREVIEW_ROW_LIMIT} of ${unmatchedFiles.length}`
                                        : `${unmatchedFiles.length} total`}
                                </small>
                            </summary>
                            <div className={styles.tableWrapper}>
                                <table className={styles.runsTable}>
                                    <thead>
                                        <tr>
                                            <th>File</th>
                                            <th>Name in file</th>
                                            <th>Date of birth</th>
                                            <th>Email in file</th>
                                            <th>Problem</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {unmatchedFiles.slice(0, PREVIEW_ROW_LIMIT).map((file) => (
                                            <tr key={file.key}>
                                                <td>{fileName(file.key)}</td>
                                                <td>{file.name || '—'}</td>
                                                <td>{file.dob || '—'}</td>
                                                <td>{file.email || '—'}</td>
                                                <td>
                                                    {file.errors?.length
                                                        ? file.errors.join('; ')
                                                        : 'This person is not an eligible, duplicate-free client in the batch'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </details>
                    )}
                    <button
                        type="button"
                        onClick={() => void commit()}
                        disabled={Boolean(busy) || importableCount === 0}
                    >
                        {busy === 'commit'
                            ? 'Importing…'
                            : 'Import eligible clients into introducer tables'}
                    </button>
                </div>
            )}
        </section>
    )
}

export const BatchAnalysis = ({
    analysis,
    batchId,
    analysedAt = null,
    filenameBase = 'viewthru-batch',
    onRerun,
    rerunning = false,
    onAnalysisRefresh,
}: {
    analysis: ViewThruBatchAnalysis
    batchId: string
    analysedAt?: Date | null
    filenameBase?: string
    onRerun?: () => void | Promise<void>
    rerunning?: boolean
    onAnalysisRefresh: () => void | Promise<void>
}) => {
    const [status, setStatus] = useState<StatusFilter>('all')
    const [reasonFilter, setReasonFilter] = useState('')
    const [search, setSearch] = useState('')
    const [sort, setSort] = useState<SortOrder>('valueDesc')
    const [clientListVisible, setClientListVisible] = useState(false)
    const [duplicateCategory, setDuplicateCategory] = useState('')
    // A second client reason the listed clients must also have failed on; only
    // meaningful while a client reason filter is active, so it resets with it.
    const [overlapFilter, setOverlapFilter] = useState('')
    const [openClient, setOpenClient] = useState<FlaggedClient | null>(null)

    useEffect(() => {
        setOverlapFilter('')
    }, [reasonFilter])

    const populations = useMemo<Record<Outcome, FlaggedClient[]>>(
        () => ({
            eligible: analysis.eligibleClients.map((client) => ({ ...client, outcome: 'eligible' })),
            ineligible: analysis.ineligibleClients.map((client) => ({ ...client, outcome: 'ineligible' })),
            duplicate: (analysis.duplicateClients || analysis.notEligibleDups || []).map((client) => ({
                ...client,
                outcome: 'duplicate',
            })),
        }),
        [analysis]
    )
    const allClients = useMemo(
        () => [...populations.eligible, ...populations.ineligible, ...populations.duplicate],
        [populations]
    )

    const clientReasonOptions = useMemo(
        () => Object.entries(analysis.reasons.clientReasons).sort((a, b) => b[1] - a[1]),
        [analysis.reasons.clientReasons]
    )
    const claimReasonOptions = useMemo(
        () => Object.entries(analysis.reasons.claimReasons).sort((a, b) => b[1] - a[1]),
        [analysis.reasons.claimReasons]
    )

    const [reasonScope, reasonKey] = reasonFilter ? reasonFilter.split('::') : ['', '']
    const highlightReason = reasonScope === 'claim' ? reasonKey : null

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase()
        const source = status === 'all' ? allClients : populations[status]
        const matches = source.filter((client) => {

            if (reasonScope === 'client' && client.clientRejectionReason !== reasonKey) return false
            if (
                reasonScope === 'client' &&
                overlapFilter &&
                !clientRejectionReasons(client).includes(overlapFilter)
            ) {
                return false
            }
            if (
                reasonScope === 'claim' &&
                !client.claims.some((claim) => claim.claimRejectionReason === reasonKey)
            ) {
                return false
            }
            if (
                status === 'duplicate' &&
                duplicateCategory &&
                !clientDuplicateCategories(client).includes(duplicateCategory)
            ) {
                return false
            }

            if (!term) return true
            return (
                client.name?.toLowerCase().includes(term) ||
                client.email?.toLowerCase().includes(term) ||
                client.claims.some(
                    (claim) =>
                        claim.policyNumber?.toLowerCase().includes(term) ||
                        claim.lender?.toLowerCase().includes(term)
                )
            )
        })

        const ordered = [...matches]
        if (sort === 'valueDesc') ordered.sort((a, b) => (b.clientTotalValue || 0) - (a.clientTotalValue || 0))
        if (sort === 'valueAsc') ordered.sort((a, b) => (a.clientTotalValue || 0) - (b.clientTotalValue || 0))
        if (sort === 'name') ordered.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        if (sort === 'agreements') ordered.sort((a, b) => (b.claimCount || 0) - (a.claimCount || 0))
        return ordered
    }, [allClients, populations, status, reasonScope, reasonKey, overlapFilter, search, sort, duplicateCategory])

    const filteredTotals = useMemo(
        () =>
            filtered.reduce(
                (totals, client) => ({
                    value: totals.value + (client.clientTotalValue || 0),
                    agreements: totals.agreements + (client.claimCount || 0),
                    eligibleAgreements: totals.eligibleAgreements + (client.eligibleClaimCount || 0),
                    matchedAgreements:
                        totals.matchedAgreements +
                        (highlightReason
                            ? client.claims.filter((claim) => claim.claimRejectionReason === highlightReason)
                                  .length
                            : 0),
                }),
                { value: 0, agreements: 0, eligibleAgreements: 0, matchedAgreements: 0 }
            ),
        [filtered, highlightReason]
    )

    const { clientGates, claimGates } = useMemo(() => buildGates(analysis.params), [analysis.params])
    const filtersActive =
        status !== 'all' || Boolean(reasonFilter) || Boolean(search.trim()) || Boolean(duplicateCategory)
    const duplicateBreakdown = useMemo(
        () => buildDuplicateBreakdown(populations.duplicate),
        [populations.duplicate]
    )
    const ineligibleBreakdown = useMemo(
        () => buildIneligibleBreakdown(populations.ineligible),
        [populations.ineligible]
    )
    const ineligibleClaimBreakdown = useMemo(
        () => buildIneligibleClaimBreakdown(populations.ineligible),
        [populations.ineligible]
    )
    const overlaps = useMemo<{ entries: OverlapEntry[]; withOverlaps: number; total: number }>(() => {
        if (reasonScope !== 'client') return { entries: [], withOverlaps: 0, total: 0 }
        const total = populations.ineligible.filter((client) => client.clientRejectionReason === reasonKey).length
        return { ...buildOverlapBreakdown(populations.ineligible, reasonKey), total }
    }, [populations.ineligible, reasonScope, reasonKey])

    const summaries = [
        ['Clients analysed', analysis.counts.clients],
        ['Agreements found', analysis.counts.agreements],
        ['Eligible clients', analysis.counts.eligibleClients],
        ['Not eligible', analysis.counts.ineligibleClients],
        ['Duplicates', analysis.counts.duplicateClients ?? analysis.duplicateClients?.length ?? analysis.counts.notEligibleDups ?? 0],
        ['Eligible agreements', analysis.counts.eligibleAgreements],
        ['Failed agreements', analysis.counts.failedAgreements],
        ['AML issues', analysis.counts.clientsWithAmlIssues],
        ['Estimated book value', money(analysis.summary.totalBookValue)],
    ]

    return (
        <section className={styles.analysisSection}>
            <div className={styles.analysisHeading}>
                <div>
                    <h3>Funding analysis</h3>
                    <p>Funding batch rules applied to this batch, read only — nothing is collected or written.</p>
                </div>
                <div className={styles.analysisHeadingMeta}>
                    <span>
                        Valued to {date(analysis.params.calculationDate)} · {analysis.params.commissionPercentage}%
                        commission · {analysis.params.statutoryInterestPercentage}% interest ·{' '}
                        {money(analysis.params.minimumClientValue)} minimum client value
                    </span>
                    <small>
                        {analysedAt
                            ? `Snapshot taken at ${analysedAt.toLocaleTimeString('en-GB', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                              })}`
                            : 'Snapshot of this batch'}
                        {onRerun && (
                            <button type="button" onClick={() => void onRerun()} disabled={rerunning}>
                                {rerunning ? 'Re-running...' : 'Re-run'}
                            </button>
                        )}
                    </small>
                    <div className={styles.analysisExports}>
                        <button
                            type="button"
                            disabled={!analysis.eligibleClients.length}
                            onClick={() =>
                                downloadAnalysisClientsCsv(
                                    analysis.eligibleClients.map((client) => ({ ...client, outcome: 'eligible' })),
                                    filenameBase,
                                    'eligible'
                                )
                            }
                        >
                            Export eligible CSV ({analysis.counts.eligibleClients})
                        </button>
                        <button
                            type="button"
                            disabled={!analysis.ineligibleClients.length}
                            onClick={() =>
                                downloadAnalysisClientsCsv(
                                    analysis.ineligibleClients.map((client) => ({ ...client, outcome: 'ineligible' })),
                                    filenameBase,
                                    'not-eligible'
                                )
                            }
                        >
                            Export not eligible CSV ({analysis.counts.ineligibleClients})
                        </button>
                        <button
                            type="button"
                            disabled={!(analysis.duplicateClients || analysis.notEligibleDups)?.length}
                            onClick={() =>
                                downloadAnalysisClientsCsv(
                                    (analysis.duplicateClients || analysis.notEligibleDups).map((client) => ({
                                        ...client,
                                        outcome: 'duplicate',
                                    })),
                                    filenameBase,
                                    'duplicates'
                                )
                            }
                        >
                            Export duplicates CSV ({analysis.counts.duplicateClients ?? analysis.counts.notEligibleDups ?? 0})
                        </button>
                    </div>
                </div>
            </div>

            <div className={styles.analysisSummary}>
                {summaries.map(([label, value]) => (
                    <div key={String(label)} className={styles.summaryCard}>
                        <span className={styles.detailLabel}>{label}</span>
                        <span className={styles.detailValue}>{value}</span>
                    </div>
                ))}
            </div>

            <DuplicateCoverage deduction={analysis.duplicateDeduction} />

            <details className={styles.gateChecklist}>
                <summary>
                    Eligibility checklist
                    <small>
                        {clientGates.filter((gate) => gate.applied).length} client and{' '}
                        {claimGates.filter((gate) => gate.applied).length} agreement checks applied
                    </small>
                </summary>
                <div className={styles.gateGroups}>
                    <div>
                        <h5>Client checks</h5>
                        <GateList gates={clientGates} />
                    </div>
                    <div>
                        <h5>Agreement checks — every agreement must pass all of these</h5>
                        <GateList gates={claimGates} />
                    </div>
                </div>
            </details>

            <ImportPanel batchId={batchId} onImported={onAnalysisRefresh} />

            <button
                type="button"
                className={styles.clientListToggle}
                onClick={() => setClientListVisible((visible) => !visible)}
                aria-expanded={clientListVisible}
            >
                <span>
                    {clientListVisible ? 'Hide analysed clients' : 'Show analysed clients'}
                    <small>
                        {analysis.counts.eligibleClients} eligible · {analysis.counts.ineligibleClients} not
                        eligible · {populations.duplicate.length} duplicates · {allClients.length} total
                    </small>
                </span>
            </button>

            {clientListVisible && (
            <>
            <div className={styles.analysisFilters}>
                <div className={styles.filterGroup}>
                    <span className={styles.detailLabel}>Outcome</span>
                    <div className={styles.segmented}>
                        {(
                            [
                                ['all', `All (${allClients.length})`],
                                ['eligible', `Eligible (${analysis.counts.eligibleClients})`],
                                ['ineligible', `Not eligible (${analysis.counts.ineligibleClients})`],
                                ['duplicate', `Duplicates (${populations.duplicate.length})`],
                            ] as [StatusFilter, string][]
                        ).map(([value, label]) => (
                            <button
                                key={value}
                                type="button"
                                className={status === value ? styles.segmentedActive : ''}
                                onClick={() => {
                                    setStatus(value)
                                    if (value !== 'duplicate') setDuplicateCategory('')
                                }}
                                aria-pressed={status === value}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                <label className={styles.filterGroup}>
                    <span className={styles.detailLabel}>Failure</span>
                    <select value={reasonFilter} onChange={(event) => setReasonFilter(event.target.value)}>
                        <option value="">Any failure or none</option>
                        {clientReasonOptions.length > 0 && (
                            <optgroup label="Client rejected for">
                                {clientReasonOptions.map(([reason, count]) => (
                                    <option key={reason} value={`client::${reason}`}>
                                        {reasonLabel(reason)} ({count})
                                    </option>
                                ))}
                            </optgroup>
                        )}
                        {claimReasonOptions.length > 0 && (
                            <optgroup label="Has an agreement rejected for">
                                {claimReasonOptions.map(([reason, count]) => (
                                    <option key={reason} value={`claim::${reason}`}>
                                        {reasonLabel(reason)} ({count})
                                    </option>
                                ))}
                            </optgroup>
                        )}
                    </select>
                </label>

                <label className={styles.filterGroup}>
                    <span className={styles.detailLabel}>Search</span>
                    <input
                        type="search"
                        value={search}
                        placeholder="Name, email, agreement or lender"
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </label>

                <label className={styles.filterGroup}>
                    <span className={styles.detailLabel}>Sort</span>
                    <select value={sort} onChange={(event) => setSort(event.target.value as SortOrder)}>
                        <option value="valueDesc">Value, high to low</option>
                        <option value="valueAsc">Value, low to high</option>
                        <option value="agreements">Agreement count</option>
                        <option value="name">Name</option>
                    </select>
                </label>

                {filtersActive && (
                    <button
                        type="button"
                        className={styles.filterReset}
                        onClick={() => {
                            setStatus('all')
                            setReasonFilter('')
                            setSearch('')
                            setDuplicateCategory('')
                        }}
                    >
                        Clear filters
                    </button>
                )}
            </div>

            {status === 'ineligible' && (
                <div className={`${styles.duplicateBreakdown} ${styles.ineligibleBreakdown}`}>
                    <strong>Not eligible clients by reason</strong>
                    <p>
                        {populations.ineligible.length} {populations.ineligible.length === 1 ? 'client' : 'clients'}{' '}
                        failed. Each client is counted once, under the first check that rejected them. Click a
                        reason to filter the list below.
                    </p>
                    {ineligibleBreakdown.length ? (
                        <div className={styles.ineligibleBreakdownGroups}>
                            {ineligibleBreakdown.map((section) => (
                                <div key={section.group}>
                                    <h5>{section.group}</h5>
                                    <ul>
                                        {section.entries.map((entry) => {
                                            const filterValue = `client::${entry.key}`
                                            const active = reasonFilter === filterValue
                                            return (
                                                <li key={entry.key}>
                                                    <button
                                                        type="button"
                                                        className={active ? styles.duplicateBreakdownActive : undefined}
                                                        onClick={() =>
                                                            setReasonFilter((current) =>
                                                                current === filterValue ? '' : filterValue
                                                            )
                                                        }
                                                        aria-pressed={active}
                                                    >
                                                        <span>
                                                            {entry.label}
                                                            {entry.detail && <small>{entry.detail}</small>}
                                                        </span>
                                                        <span>
                                                            {entry.count}
                                                            <small>{money(entry.value)}</small>
                                                        </span>
                                                    </button>
                                                </li>
                                            )
                                        })}
                                    </ul>
                                </div>
                            ))}
                            {ineligibleClaimBreakdown.length > 0 && (
                                <div>
                                    <h5>Agreement failures among these clients</h5>
                                    <ul>
                                        {ineligibleClaimBreakdown.map((entry) => {
                                            const filterValue = `claim::${entry.key}`
                                            const active = reasonFilter === filterValue
                                            return (
                                                <li key={entry.key}>
                                                    <button
                                                        type="button"
                                                        className={active ? styles.duplicateBreakdownActive : undefined}
                                                        onClick={() =>
                                                            setReasonFilter((current) =>
                                                                current === filterValue ? '' : filterValue
                                                            )
                                                        }
                                                        aria-pressed={active}
                                                    >
                                                        <span>{entry.label}</span>
                                                        <span>
                                                            {entry.clients}
                                                            <small>
                                                                {entry.agreements}{' '}
                                                                {entry.agreements === 1 ? 'agreement' : 'agreements'}
                                                            </small>
                                                        </span>
                                                    </button>
                                                </li>
                                            )
                                        })}
                                    </ul>
                                </div>
                            )}
                        </div>
                    ) : (
                        <p>No rejection reasons were recorded for these clients.</p>
                    )}

                    {reasonScope === 'client' && overlaps.entries.length > 0 && (
                        <details className={styles.overlaps} open={Boolean(overlapFilter)}>
                            <summary>
                                Overlapping failures for {reasonLabel(reasonKey)}
                                <small>
                                    {overlaps.withOverlaps} of {overlaps.total} also failed at least one other check
                                </small>
                            </summary>
                            <p>
                                Pick another check to narrow the list to clients who failed both. Each client
                                stays counted once above, under the first check that rejected them.
                            </p>
                            <div className={styles.overlapList}>
                                {overlaps.entries.map((entry) => (
                                    <button
                                        key={entry.key}
                                        type="button"
                                        className={overlapFilter === entry.key ? styles.overlapActive : undefined}
                                        aria-pressed={overlapFilter === entry.key}
                                        onClick={() =>
                                            setOverlapFilter((current) => (current === entry.key ? '' : entry.key))
                                        }
                                    >
                                        <span>{entry.label}</span>
                                        <span>{entry.count}</span>
                                    </button>
                                ))}
                            </div>
                        </details>
                    )}
                </div>
            )}

            {status === 'duplicate' && (
                <div className={styles.duplicateBreakdown}>
                    <strong>Duplicate clients by match type</strong>
                    <p>
                        {populations.duplicate.length} unique {populations.duplicate.length === 1 ? 'client' : 'clients'}.
                        A person counted in more than one group has more than one kind of match.
                    </p>
                    {duplicateBreakdown.length ? (
                        <ul>
                            {duplicateBreakdown.map((entry) => (
                                <li key={entry.key}>
                                    <button
                                        type="button"
                                        className={
                                            duplicateCategory === entry.key
                                                ? styles.duplicateBreakdownActive
                                                : undefined
                                        }
                                        onClick={() =>
                                            setDuplicateCategory((current) =>
                                                current === entry.key ? '' : entry.key
                                            )
                                        }
                                        aria-pressed={duplicateCategory === entry.key}
                                    >
                                        <span>{entry.label}</span>
                                        <span>{entry.count}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p>No match-type details were recorded for these duplicates.</p>
                    )}
                </div>
            )}

            <div className={styles.filterResultLine}>
                <span>
                    {filtered.length} of {status === 'all' ? allClients.length : populations[status].length} clients
                </span>
                <span>{money(filteredTotals.value)} valued</span>
                <span>
                    {filteredTotals.eligibleAgreements} of {filteredTotals.agreements} agreements eligible
                </span>
                {highlightReason && (
                    <span>
                        {filteredTotals.matchedAgreements} agreements match {reasonLabel(highlightReason)}
                    </span>
                )}
                {overlapFilter && (
                    <span>
                        also failed {reasonLabel(overlapFilter)}{' '}
                        <button type="button" className={styles.inlineClear} onClick={() => setOverlapFilter('')}>
                            clear
                        </button>
                    </span>
                )}
            </div>

            <ClientList clients={filtered} highlightReason={highlightReason} onOpen={setOpenClient} />
            </>
            )}

            {openClient && (
                <AnalysisClientModal
                    client={openClient}
                    batchId={batchId}
                    batchLabel={filenameBase}
                    onClose={() => setOpenClient(null)}
                />
            )}
        </section>
    )
}
